import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import * as businessService from '../../data-layer/business/service';
import { BusinessStatsModel } from '../../data-layer/business/types';
import { BusinessStatisticSummary as BusinessStatisticSummaryUI } from '../../ui/business/BusinessStatisticSummary/BusinessStatisticSummary';
import { BusinessStatisticSkeleton } from '../../ui/business/BusinessStatisticSummary/parts/BusinessStatisticSkeleton/BusinessStatisticSkeleton';
import { Flex } from '../../ui/layout/Flex';
import { FeedManagementDateDropdown } from '../../ui/posts/FeedManagement/parts/FeedManagementDateDropdown/FeedManagementDateDropdown';
import { hasAccessToBusinessStat } from '../../utils/accessService';
import { useCurrentUser } from '../users/useCurrentUser';

const DEFAULT_STAT_PERIOD = '7';

interface BusinessStatisticProps {
    stats: BusinessStatsModel;
}

export const BusinessStatisticSummary = () => {
    const { businessId } = useParams();
    const [period, setPeriod] = useState<string | null>(DEFAULT_STAT_PERIOD);
    const [dateFrom, setDateFrom] = useState<string | null>(null);
    const [dateTo, setDateTo] = useState<string | null>(null);
    const [businessStatistic, setBusinessStatistic] = useState<BusinessStatisticProps | undefined>(
        undefined,
    );
    const currentUser = useCurrentUser();
    const { loading: statisticLoading } = useAsync(async () => {
        setBusinessStatistic(undefined);
        if (businessId && hasAccessToBusinessStat(businessId, currentUser)) {
            setBusinessStatistic(
                await businessService.loadBusinessStatistic(
                    businessId,
                    period === 'undefined' ? undefined : period,
                    dateFrom,
                    dateTo,
                ),
            );
        }
    }, [businessId, period, dateFrom, dateTo]);

    const onChangeDate = useCallback(
        (value: string | null, dateFromValue?: string, dateToValue?: string) => {
            setPeriod(value);
            setDateFrom(dateFromValue || null);
            setDateTo(dateToValue || null);
        },
        [],
    );

    return (
        <Flex
            borderRadius={2}
            background="var(--background-primary)"
            width="100%"
            overflow="hidden"
            mb={3}
        >
            <Flex direction="row" width="100%" p={2} alignItems="center" zIndex={2}>
                <FeedManagementDateDropdown
                    value={period}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onChange={onChangeDate}
                />
            </Flex>
            {statisticLoading ? (
                <BusinessStatisticSkeleton />
            ) : (
                businessStatistic?.stats && (
                    <BusinessStatisticSummaryUI statistic={businessStatistic.stats} />
                )
            )}
        </Flex>
    );
};
